import React from 'react';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import { Box, Button, Checkbox, Divider, FormControlLabel, FormHelperText, InputLabel, MenuItem, OutlinedInput, Select, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import InputMask from 'react-input-mask';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import PageHeader from '../../../components/page-header';
import { useParlamentarianCreateEditController } from './context/parlamentarian-create-edit-controller';
import { useParlamentarianCreateEditState } from './context/parlamentarian-create-edit-context';

export const TIPO_PARLAMENTAR = [
    { value: 1, label: 'Deputado Estadual' },
    { value: 2, label: 'Deputado Federal' },
    { value: 3, label: 'Senador' },
];

export function ParlamentarianCreateEditComponent() {
    const { handleOnChange, handleContactChange, handleSave, handleCancel, isSaving, politicalParties } = useParlamentarianCreateEditController();
    const { parlamentarianRequest, showValidationErrors, parlamentarianId } = useParlamentarianCreateEditState();
    const isEdit = !!parlamentarianId;

    return (
        <React.Fragment>
            <Grid item xs={12}>
                <PageHeader
                    title={isEdit ? 'Editar Parlamentar' : 'Novo Parlamentar'}
                    subtitle={isEdit ? 'Atualize as informações do parlamentar' : 'Cadastre um novo parlamentar no sistema'}
                    breadcrumbs={[{ label: 'Parlamentares', href: '/parlamentares' }, { label: isEdit ? 'Editar' : 'Novo' }]}
                />
            </Grid>

            <Grid item xs={12}>
                <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column' }} elevation={3}>
                    <Grid container spacing={3}>

                        {/* Identificação */}
                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                                Identificação
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                        </Grid>

                        <Grid item xs={12} md={9} lg={9}>
                            <InputLabel required>Nome Completo</InputLabel>
                            <OutlinedInput
                                fullWidth
                                autoComplete="off"
                                name="name"
                                value={parlamentarianRequest.name || ''}
                                onChange={handleOnChange}
                                error={showValidationErrors && !parlamentarianRequest.name}
                            />
                            {showValidationErrors && !parlamentarianRequest.name && (
                                <FormHelperText error>Nome é obrigatório</FormHelperText>
                            )}
                        </Grid>
                        <Grid item xs={12} md={3} lg={3} sx={{ display: 'flex', alignItems: 'flex-end', pb: 0.5 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name="isActive"
                                        checked={!!parlamentarianRequest.isActive}
                                        onChange={handleOnChange}
                                    />
                                }
                                label="Parlamentar Ativo"
                            />
                        </Grid>
                        <Grid item xs={12} md={5} lg={5}>
                            <InputLabel required>Tipo</InputLabel>
                            <Select
                                fullWidth
                                name="type"
                                value={parlamentarianRequest.type ?? ''}
                                onChange={handleOnChange}
                                error={showValidationErrors && !parlamentarianRequest.type}
                            >
                                <MenuItem value=""><em>Selecionar</em></MenuItem>
                                {TIPO_PARLAMENTAR.map((t) => (
                                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                                ))}
                            </Select>
                            {showValidationErrors && !parlamentarianRequest.type && (
                                <FormHelperText error>Tipo é obrigatório</FormHelperText>
                            )}
                        </Grid>
                        <Grid item xs={12} md={7} lg={7}>
                            <InputLabel>Partido Político</InputLabel>
                            <Select
                                fullWidth
                                name="politicalPartyId"
                                value={parlamentarianRequest.politicalPartyId ?? ''}
                                onChange={handleOnChange}
                            >
                                <MenuItem value=""><em>Nenhum</em></MenuItem>
                                {politicalParties.map((p) => (
                                    <MenuItem key={p.id} value={p.id}>{p.abbreviation} — {p.name}</MenuItem>
                                ))}
                            </Select>
                        </Grid>

                        {/* Contato */}
                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{ mb: 2, mt: 2, color: 'primary.main', fontWeight: 600 }}>
                                Contato
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                        </Grid>

                        <Grid item xs={12} md={4} lg={4}>
                            <InputLabel>E-mail</InputLabel>
                            <OutlinedInput
                                fullWidth
                                autoComplete="off"
                                name="email"
                                type="email"
                                value={parlamentarianRequest.contact?.email || ''}
                                onChange={handleContactChange}
                            />
                        </Grid>

                        <Grid item xs={12} md={3} lg={3}>
                            <InputLabel>Telefone</InputLabel>
                            <InputMask
                                mask="(99) 9999-9999"
                                name="phone"
                                value={parlamentarianRequest.contact?.phone || ''}
                                onChange={handleContactChange}
                            >
                                <OutlinedInput fullWidth name="phone" autoComplete="off" />
                            </InputMask>
                        </Grid>

                        <Grid item xs={12} md={3} lg={3}>
                            <InputLabel>Celular</InputLabel>
                            <InputMask
                                mask="(99) 99999-9999"
                                name="mobile"
                                value={parlamentarianRequest.contact?.mobile || ''}
                                onChange={handleContactChange}
                            >
                                <OutlinedInput fullWidth name="mobile" autoComplete="off" />
                            </InputMask>
                        </Grid>

                        <Grid item xs={12} md={2} lg={2}>
                            <InputLabel>Tel. Comercial</InputLabel>
                            <InputMask
                                mask="(99) 9999-9999"
                                name="commercialPhone"
                                value={parlamentarianRequest.contact?.commercialPhone || ''}
                                onChange={handleContactChange}
                            >
                                <OutlinedInput fullWidth name="commercialPhone" autoComplete="off" />
                            </InputMask>
                        </Grid>

                        {/* Ações */}
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2, mt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                <Box sx={{ flex: '1 1 auto' }} />
                                <Button
                                    variant="outlined"
                                    startIcon={<CancelIcon />}
                                    onClick={handleCancel}
                                    sx={{ mr: 1 }}
                                >
                                    Cancelar
                                </Button>
                                <LoadingButton
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    onClick={handleSave}
                                    loading={isSaving}
                                    disabled={isSaving}
                                >
                                    Salvar
                                </LoadingButton>
                            </Box>
                        </Grid>

                    </Grid>
                </Paper>
            </Grid>
        </React.Fragment>
    );
}
